import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useLocation } from "@tanstack/react-router";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { customerQuery } from "@/api/customers";
import { establishmentQuery } from "@/api/establishment";
import { useAuth } from "@/auth/AuthProvider";
import { ClienteConversas } from "@/components/clientes/ClienteConversas";
import { ClienteFicha } from "@/components/clientes/ClienteFicha";
import { ClienteHistorico } from "@/components/clientes/ClienteHistorico";
import { ClienteLgpd } from "@/components/clientes/ClienteLgpd";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authed/clientes/$customerId")({
  staticData: { title: "Ficha do cliente" },
  component: ClienteFichaPage,
});

function ClienteFichaPage() {
  const { customerId } = Route.useParams();
  const { user } = useAuth();
  const establishmentId = user?.establishment_id ?? "";

  const customer = useQuery(customerQuery(customerId));
  const establishment = useQuery({
    ...establishmentQuery(establishmentId),
    enabled: Boolean(establishmentId),
  });

  // "Conversa", na lista de clientes, aponta pra esta ficha com `#conversas`.
  // Quem rola até lá é a própria seção, quando as conversas chegam (ver
  // ClienteConversas) — o router não tem como: na hora da navegação a seção
  // ainda nem existe.
  const { hash } = useLocation();
  const focarConversas = hash.replace("#", "") === "conversas";

  return (
    <div className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <Link
        to="/clientes"
        // `min-h-11 -my-2`: 20px de altura era o alvo do único caminho de
        // volta pra listagem no celular. Cresce sem empurrar o conteúdo.
        className="-my-2 inline-flex min-h-11 items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Clientes
      </Link>

      <div className="mt-4 space-y-6">
        {customer.isPending || establishment.isPending ? (
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : customer.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Cliente não encontrado — pode ter sido excluído (anonimizado).
          </p>
        ) : (
          customer.data &&
          establishment.data && (
            <>
              <ClienteFicha
                customer={customer.data}
                timeZone={establishment.data.timezone}
              />
              <Separator />
              <ClienteHistorico
                customerId={customerId}
                timeZone={establishment.data.timezone}
              />
              <Separator />
              {/* Âncora: a lista de clientes linka direto pra cá quando o dono
                  clica em "Conversa" numa linha. */}
              <div id="conversas">
                <ClienteConversas
                  customerId={customerId}
                  timeZone={establishment.data.timezone}
                  focar={focarConversas}
                />
              </div>
              <Separator />
              {/*
               * LGPD fechada por padrão: exportar dados e registrar
               * consentimento são ações raras, e abertas ocupavam mais espaço
               * que a ficha inteira — o que o dono abre a ficha pra ver (quem
               * é, quando veio, o que já fez) ficava embaixo delas.
               */}
              <Collapsible>
                <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 rounded-md border bg-card px-4 py-3 text-left transition-colors hover:bg-muted/60">
                  <span>
                    <span className="text-sm font-medium">Privacidade (LGPD)</span>
                    <span className="block text-xs text-muted-foreground">
                      Exportar dados, registrar consentimento e excluir o cliente
                    </span>
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-4">
                  <ClienteLgpd
                    customerId={customerId}
                    customerName={customer.data.name}
                  />
                </CollapsibleContent>
              </Collapsible>
            </>
          )
        )}
      </div>
    </div>
  );
}
